import {calculateQuizSessionScoreFromQuestions, type QuizSessionWithScore} from '@cp/model';
import {ContentContainer} from '@coolcolduk/ui-mui';
import {Box, SelectChangeEvent, Typography} from '@mui/material';
import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {PrivateRouteProps} from '../../config/routeType';
import useListExamModules from '../../hooks/examModules/useListExamModules';
import useListUserSessions, {sessionState} from '../../hooks/quizSession/useListUserSessions';
import LoadingPage from '../other/LoadingPage';
import SessionsFilters, {type SessionStatus} from './SessionsFilters';
import SessionsList from './SessionsList';
type ModuleFilter = 'all' | string;

function sessionWithScore(
  session: {questions: Array<{isCorrect?: boolean}>; [k: string]: unknown},
  isCompleted: boolean,
): QuizSessionWithScore & {isCompleted: boolean} {
  const score = calculateQuizSessionScoreFromQuestions(session.questions);
  return {...session, score, isCompleted} as QuizSessionWithScore & {isCompleted: boolean};
}

export default function SessionsPage(_props: PrivateRouteProps) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SessionStatus>('all');
  const [moduleFilter, setModuleFilter] = useState<ModuleFilter>('all');

  // Fetch sessions data
  const {sessions: activeSessions, loading: activeLoading} = useListUserSessions({
    state: sessionState.active,
    limit: 10,
    offset: 0,
  });
  const {sessions: completedSessions, loading: completedLoading} = useListUserSessions({
    state: 'completed',
    limit: 10,
    offset: 0,
  });
  const {examModules, loading: modulesLoading} = useListExamModules();

  // Combine all sessions (add score and isCompleted for list type)
  const allSessions = useMemo(() => {
    const active = activeSessions.map((s) => sessionWithScore(s, false));
    const completed = completedSessions.map((s) => sessionWithScore(s, true));
    return [...active, ...completed].sort(
      (a, b) => new Date(b.startedAt as Date).getTime() - new Date(a.startedAt as Date).getTime(),
    );
  }, [activeSessions, completedSessions]);

  // Filter sessions based on selected filters
  const filteredSessions = useMemo(
    () =>
      allSessions.filter((session) => {
        const statusMatch =
          statusFilter === 'all' ||
          (statusFilter === 'active' && !session.isCompleted) ||
          (statusFilter === 'completed' && session.isCompleted);

        const moduleMatch = moduleFilter === 'all' || session.moduleId === moduleFilter;

        return statusMatch && moduleMatch;
      }),
    [allSessions, statusFilter, moduleFilter],
  );

  const handleSessionClick = (session: QuizSessionWithScore & {isCompleted: boolean}) => {
    if (session.isCompleted) {
      navigate(`/session/${session.id}/complete`);
    } else {
      navigate(`/session/${session.id}`);
    }
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<SessionStatus>) => {
    setStatusFilter(event.target.value as SessionStatus);
  };

  const handleModuleFilterChange = (event: SelectChangeEvent<ModuleFilter>) => {
    setModuleFilter(event.target.value as ModuleFilter);
  };

  if (activeLoading || completedLoading || modulesLoading) {
    return <LoadingPage />;
  }

  return (
    <ContentContainer data-testid="sessions-page">
      <Typography variant="h4" gutterBottom>
        Sessions
      </Typography>

      <SessionsFilters
        statusFilter={statusFilter}
        moduleFilter={moduleFilter}
        examModules={examModules}
        onStatusFilterChange={handleStatusFilterChange}
        onModuleFilterChange={handleModuleFilterChange}
      />

      <Box sx={{mt: 3}}>
        <SessionsList sessions={filteredSessions} onSessionClick={handleSessionClick} />
      </Box>
    </ContentContainer>
  );
}
